<template>
  <div>
    <v-data-table-virtual
      :items="shifts"
      :headers="headers"
      :sort-by="[{ key: 'date', order: 'desc' }, { key: 'begins', order: 'asc' }]"
    />
    <div class="text-body-large mt-2">
      {{ t('shifts.total', { count: shifts?.length }) }}
    </div>
  </div>
</template>

<script setup lang="ts">
import type { DataTableHeader } from 'vuetify';
import axios from 'axios';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDate, useSettings } from '@/composables';
import { useErrorStore } from '@/stores/errors';

const props = defineProps<{
  userId: number;
}>();

const { t } = useI18n();
const { toLongDay } = useDate();
const { showSnackMessage } = useErrorStore();
const { getSetting } = useSettings();
/** A shift the user is attached to; `pivot` carries their status on it. */
type UserShift = App.Models.Shift & {
  pivot: { status: string; cancellation_reason_id: number | null };
};

const shifts = ref<UserShift[]>([]);
const cancellationReasons = ref<App.Models.ShiftCancellationReason[]>([]);

const headers: DataTableHeader<UserShift>[] = [
  {
    title: t('global.status'),
    key: 'pivot.status',
    value: (item) => t(`shift.status.${item.pivot.status}`),
  },
  {
    title: t('location.title'),
    key: 'location.name',
  },
  {
    title: t('date.title'),
    key: 'date',
  },
  {
    title: t('date.day'),
    key: 'dow',
    value: (item) => toLongDay(item.date),
  },
  {
    title: getSetting('captain_label') ?? '',
    key: 'captain_id',
    value: (item) => item.captain_id === props.userId ? t('global.yes') : t('global.no'),
  },
  {
    title: t('shift_tag.is_training_shift'),
    key: 'is_training_shift',
    value: (item) => (item.tags ?? []).some(tag => tag.is_training_shift === true) ? t('global.yes') : t('global.no'),
  },
  {
    title: t('date.start_time'),
    key: 'begins',
  },
  {
    title: t('date.ends'),
    key: 'ends',
  },
  {
    title: t('cancellation_reason.title'),
    key: 'pivot.cancellation_reason_id',
    value: (item) => cancellationReasons.value.find((a) => a.id === item.pivot.cancellation_reason_id)?.reason ?? '-',
  },
];

async function loadItems() {
  try {
    const response = await axios.get(`/api/users/${props.userId}?inc[]=shifts&inc[]=shifts.location&inc[]=shifts.tags`);
    shifts.value = response.data.data.shifts.filter((shift: UserShift) => (shift.pivot.status === 'cancelled' || shift.pivot.status === 'assigned'));
    cancellationReasons.value = (await axios.get('/api/shiftcancellationreasons?per_page=1000')).data.data;
  }
  catch (error) {
    console.error(error);
    showSnackMessage(t('errors.cannot_load'));
  }
}

onMounted(() => {
  loadItems();
});
</script>
